/**
 * MentionMarker — renders one @-mention attachment (a source section or table)
 * as a compact asset chip: a type icon + the truncated asset name.
 *
 * The composer stores a picked asset as a bare ``@[n]`` marker in the message
 * text plus a structured ``MentionAttachment`` alongside. That marker reads
 * like a footnote once the message is sent; this component is what the
 * transcript (and the composer's chip row) render in its place so the writer
 * sees *what* they tagged — a table icon vs a section icon and the name —
 * rather than an opaque ``[1]``.
 *
 * Presentational only: the caller supplies the resolved attachment; truncation
 * is visual (the full label stays in the ``title`` tooltip and the aria-label).
 */

import { makeStyles } from "@fluentui/react-components";
import { DocumentText16Regular, Table16Regular } from "@fluentui/react-icons";
import React from "react";
import { brandColors, surfaceColors } from "../tokens";

/** Visible-name cap; the full label rides on the tooltip + aria-label. */
const LABEL_MAX = 24;

function truncate(label: string): string {
  const trimmed = label.trim();
  return trimmed.length > LABEL_MAX ? `${trimmed.slice(0, LABEL_MAX - 1)}…` : trimmed;
}

const useStyles = makeStyles({
  marker: {
    display: "inline-flex",
    alignItems: "center",
    gap: "3px",
    padding: "0 6px",
    borderRadius: "4px",
    background: brandColors.light,
    color: brandColors.primary,
    boxShadow: `inset 0 0 0 1px ${surfaceColors.lineStrong}`,
    fontWeight: 600,
    // Sit snugly mid-sentence without disturbing line height.
    lineHeight: 1.4,
    verticalAlign: "baseline",
    // Never let a long (untruncated edge case) name blow out the bubble.
    maxWidth: "100%",
  },
  icon: {
    flexShrink: 0,
    fontSize: "12px",
  },
  label: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
});

export interface MentionMarkerProps {
  assetType: "section" | "table";
  /** Full asset name (section title / table caption). Truncated for display. */
  label: string;
  /** Source document name — appended to the tooltip when present. */
  fileName?: string;
}

export const MentionMarker: React.FC<MentionMarkerProps> = ({ assetType, label, fileName }) => {
  const styles = useStyles();
  const Icon = assetType === "table" ? Table16Regular : DocumentText16Regular;
  const tooltip = fileName ? `${label} — ${fileName}` : label;
  return (
    <span className={styles.marker} title={tooltip} aria-label={`${assetType}: ${label}`}>
      <Icon className={styles.icon} aria-hidden="true" />
      <span className={styles.label}>{truncate(label)}</span>
    </span>
  );
};

export default MentionMarker;
